import React, { useState, useEffect, useCallback } from 'react';
import { LocalizationProvider, TimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TrashIcon, PlusIcon, CalendarIcon, FunnelIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import Helper from "../helpers";
import config from '../config';
import LayoutWrapper from '../components/LayoutWrapper';
import { format } from 'date-fns';

const baseUrl = config.apiBaseUrl;
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const BulkSessionManagement = () => {
    // Tab state
    const [activeTab, setActiveTab] = useState('add'); // 'add' or 'manage'

    // Venue state
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [currentVenueKey, setCurrentVenueKey] = useState("");

    // Add sessions state
    const [selectedDays, setSelectedDays] = useState([]);
    const [sessionsToAdd, setSessionsToAdd] = useState([]);
    const [sessionTimes, setSessionTimes] = useState([dayjs().add(1, 'hour')]);
    const [sessionLength, setSessionLength] = useState(1);
    const [sessionSeats, setSessionSeats] = useState(1);
    const [sessionTokenCost, setSessionTokenCost] = useState(1);
    const [startDate, setStartDate] = useState(dayjs());
    const [numWeeks, setNumWeeks] = useState(1);
    const [sessionName, setSessionName] = useState("");

    // Manage sessions state
    const [existingSessions, setExistingSessions] = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([]);
    const [selectedSessions, setSelectedSessions] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(dayjs());
    const [toDate, setToDate] = useState(dayjs().add(4, 'week'));

    // Bulk delete filters
    const [deleteFilterDay, setDeleteFilterDay] = useState('');
    const [deleteFilterTime, setDeleteFilterTime] = useState('');
    const [deleteFilterWeeks, setDeleteFilterWeeks] = useState(1);
    const [deleteFilterStartDate, setDeleteFilterStartDate] = useState(dayjs());

    // Bulk seat update
    const [bulkSeatCount, setBulkSeatCount] = useState(1);

    useEffect(() => {
        Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
    }, []);

    const fetchExistingSessions = useCallback(async () => {
        setLoading(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const from = fromDate.toISOString();
            const to = toDate.toISOString();

            const response = await fetch(`${baseUrl}/api/sessions/available/${currentVenueKey}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&includeusers=false`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to fetch sessions');
            }

            const data = await response.json();
            setExistingSessions(data);
            setFilteredSessions(data);
        } catch (error) {
            console.error('Error fetching sessions', error);
            alert('Failed to fetch sessions');
        } finally {
            setLoading(false);
        }
    }, [currentVenueKey, fromDate, toDate]);

    useEffect(() => {
        if (currentVenueKey && activeTab === 'manage') {
            fetchExistingSessions();
        }
    }, [currentVenueKey, activeTab, fromDate, toDate, fetchExistingSessions]);

    // Add sessions functions
    const handleDayChange = (day) => {
        setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    };

    const handleAddSession = () => {
        if (sessionsToAdd.length >= 1000) {
            alert('You can only add up to 1000 sessions at a time.');
            return;
        }

        const newSessions = [];
        for (let i = 0; i < numWeeks; i++) {
            selectedDays.forEach(day => {
                sessionTimes.forEach(time => {
                    const sessionDate = startDate.add(i, 'week').day(daysOfWeek.indexOf(day) + 1);
                    newSessions.push({
                        day,
                        date: sessionDate,
                        time,
                        length: sessionLength,
                        seats: sessionSeats,
                        tokenCost: sessionTokenCost,
                        name: sessionName,
                    });
                });
            });
        }

        setSessionsToAdd([...sessionsToAdd, ...newSessions]);
    };

    const handleRemoveSessionToAdd = (index) => {
        const updatedSessions = sessionsToAdd.filter((_, i) => i !== index);
        setSessionsToAdd(updatedSessions);
    };

    const handleAddTime = () => {
        setSessionTimes([...sessionTimes, dayjs().add(1, 'hour')]);
    };

    const handleRemoveTime = (index) => {
        const updatedTimes = sessionTimes.filter((_, i) => i !== index);
        setSessionTimes(updatedTimes);
    };

    const handleClearAddForm = () => {
        setSessionsToAdd([]);
        setSessionTimes([dayjs().add(1, 'hour')]);
        setSessionLength(1);
        setSessionSeats(1);
        setSessionTokenCost(1);
        setStartDate(dayjs());
        setNumWeeks(1);
        setSessionName("");
        setSelectedDays([]);
    };

    const handleSubmitSessions = async () => {
        if (sessionsToAdd.length === 0) {
            alert('No sessions to add');
            return;
        }

        const sessionData = sessionsToAdd.map(session => ({
            // No trailing Z: this is a local wall-clock time, not UTC (see BulkClass.js for
            // the full explanation of the display-time drift this used to cause).
            startDate: `${session.date.format('YYYY-MM-DD')}T${session.time.format('HH:mm:00')}`,
            availableSeats: session.seats,
            numberOfMinuets: session.length * 60,
            name: session.name,
            tokenCost: session.tokenCost,
        }));

        const token = await Helper.getCookie('wh-jwt');

        try {
            const response = await fetch(`${baseUrl}/api/Sessions/AddSession/${currentVenueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(sessionData),
            });

            if (response.ok) {
                alert('Sessions added successfully');
                handleClearAddForm();
            } else {
                alert('Failed to add sessions');
            }
        } catch (error) {
            console.error('Error adding sessions', error);
            alert('Failed to add sessions');
        }
    };

    // Manage sessions functions
    const handleSelectSession = (sessionId) => {
        const newSelected = new Set(selectedSessions);
        if (newSelected.has(sessionId)) {
            newSelected.delete(sessionId);
        } else {
            newSelected.add(sessionId);
        }
        setSelectedSessions(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedSessions.size === filteredSessions.length) {
            setSelectedSessions(new Set());
        } else {
            setSelectedSessions(new Set(filteredSessions.map(s => s.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedSessions.size === 0) {
            alert('No sessions selected');
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to delete ${selectedSessions.size} session(s)?`);
        if (!confirmed) return;

        const token = await Helper.getCookie('wh-jwt');
        let successCount = 0;
        let failCount = 0;

        for (const sessionId of selectedSessions) {
            try {
                const response = await fetch(`${baseUrl}/api/sessions/admin/delete/${sessionId}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (error) {
                console.error('Error deleting session', error);
                failCount++;
            }
        }

        alert(`Deleted ${successCount} session(s). Failed: ${failCount}`);
        setSelectedSessions(new Set());
        await fetchExistingSessions();
    };

    const handleFilterByDayAndTime = () => {
        if (!deleteFilterDay && !deleteFilterTime) {
            setFilteredSessions(existingSessions);
            return;
        }

        const filtered = existingSessions.filter(session => {
            const sessionDate = new Date(session.startDate);
            const sessionDay = daysOfWeek[sessionDate.getDay() === 0 ? 6 : sessionDate.getDay() - 1];
            const sessionTime = format(sessionDate, 'HH:mm');

            const dayMatch = !deleteFilterDay || sessionDay === deleteFilterDay;
            const timeMatch = !deleteFilterTime || sessionTime === deleteFilterTime;

            return dayMatch && timeMatch;
        });

        setFilteredSessions(filtered);
    };

    const handleSmartBulkDelete = () => {
        if (!deleteFilterDay || !deleteFilterTime) {
            alert('Please select both day and time for smart bulk delete');
            return;
        }

        const filtered = [];
        const startDateObj = deleteFilterStartDate;

        for (let i = 0; i < deleteFilterWeeks; i++) {
            const targetDate = startDateObj.add(i, 'week');

            existingSessions.forEach(session => {
                const sessionDate = dayjs(session.startDate);
                const sessionDay = daysOfWeek[sessionDate.day() === 0 ? 6 : sessionDate.day() - 1];
                const sessionTime = sessionDate.format('HH:mm');
                const targetDateFormatted = targetDate.format('YYYY-MM-DD');
                const sessionDateFormatted = sessionDate.format('YYYY-MM-DD');

                if (sessionDay === deleteFilterDay &&
                    sessionTime === deleteFilterTime &&
                    sessionDateFormatted === targetDateFormatted) {
                    filtered.push(session.id);
                }
            });
        }

        if (filtered.length === 0) {
            alert('No sessions match the criteria');
            return;
        }

        setSelectedSessions(new Set(filtered));
        handleFilterByDayAndTime();
    };

    const handleBulkUpdateSeats = async () => {
        if (selectedSessions.size === 0) {
            alert('No sessions selected');
            return;
        }

        if (bulkSeatCount < 1) {
            alert('Seat count must be at least 1');
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to update ${selectedSessions.size} session(s) to have ${bulkSeatCount} available seats?`);
        if (!confirmed) return;

        const token = await Helper.getCookie('wh-jwt');

        try {
            const response = await fetch(`${baseUrl}/api/sessions/bulk-update-seats/${currentVenueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    sessionIds: Array.from(selectedSessions),
                    availableSeats: bulkSeatCount
                })
            });

            if (response.ok) {
                const result = await response.json();
                alert(`Updated ${result.successCount} session(s) successfully. Failed: ${result.failCount}`);
                setSelectedSessions(new Set());
                await fetchExistingSessions();
            } else {
                const errorText = await response.text();
                alert(`Failed to update sessions: ${errorText}`);
            }
        } catch (error) {
            console.error('Error updating seats', error);
            alert('Failed to update seats');
        }
    };

    return (
        <LayoutWrapper
            title="Bulk Session Management"
            subtitle="Add multiple sessions or manage existing sessions in bulk"
        >
            <div className="space-y-6">
                {/* Venue Selection */}
                {venueKeyOptions.length > 0 && (
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Select Venue</h3>
                        <select
                            value={currentVenueKey}
                            onChange={(e) => setCurrentVenueKey(e.target.value)}
                            className="input-field"
                        >
                            {venueKeyOptions.map((venueKey) => (
                                <option key={venueKey} value={venueKey}>
                                    {venueKey}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="card">
                    <div className="flex border-b border-gray-700">
                        <button
                            onClick={() => setActiveTab('add')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                                activeTab === 'add'
                                    ? 'border-blue-500 text-blue-400'
                                    : 'border-transparent text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            <PlusIcon className="h-5 w-5 inline-block mr-2" />
                            Bulk Add Sessions
                        </button>
                        <button
                            onClick={() => setActiveTab('manage')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                                activeTab === 'manage'
                                    ? 'border-blue-500 text-blue-400'
                                    : 'border-transparent text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            <TrashIcon className="h-5 w-5 inline-block mr-2" />
                            Manage Existing Sessions
                        </button>
                    </div>
                </div>

                {/* Add Sessions Tab */}
                {activeTab === 'add' && (
                    <div className="space-y-6">
                        {/* Day Selection */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Select Days</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                                {daysOfWeek.map(day => (
                                    <label key={day} className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg hover:bg-gray-800 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={selectedDays.includes(day)}
                                            onChange={() => handleDayChange(day)}
                                            className="w-4 h-4 text-gray-600 bg-gray-800 border-gray-600 rounded focus:ring-gray-500"
                                        />
                                        <span className="text-sm text-gray-300">{day}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Date and Time Configuration */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Date & Time Configuration</h3>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Start Date
                                        </label>
                                        <DatePicker
                                            value={startDate}
                                            format="DD/MM/YYYY"
                                            onChange={(newValue) => setStartDate(newValue)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: "outlined"
                                                }
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Number of Weeks
                                        </label>
                                        <input
                                            type="number"
                                            value={numWeeks}
                                            onChange={(e) => setNumWeeks(Number(e.target.value))}
                                            className="input-field"
                                            min="1"
                                        />
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="text-sm font-medium text-gray-300">
                                            Session Times
                                        </label>
                                        <button
                                            onClick={handleAddTime}
                                            className="btn-outline flex items-center"
                                        >
                                            <PlusIcon className="h-4 w-4 mr-2" />
                                            Add Time
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {sessionTimes.map((time, index) => (
                                            <div key={index} className="flex items-center space-x-3">
                                                <div className="flex-1">
                                                    <TimePicker
                                                        value={time}
                                                        onChange={(newValue) => {
                                                            const updatedTimes = [...sessionTimes];
                                                            updatedTimes[index] = newValue;
                                                            setSessionTimes(updatedTimes);
                                                        }}
                                                        ampm={false}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                variant: "outlined"
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {sessionTimes.length > 1 && (
                                                    <button
                                                        onClick={() => handleRemoveTime(index)}
                                                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </LocalizationProvider>
                        </div>

                        {/* Session Details */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Session Details</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Session Name
                                    </label>
                                    <input
                                        type="text"
                                        value={sessionName}
                                        onChange={(e) => setSessionName(e.target.value)}
                                        className="input-field"
                                        placeholder="Enter session name"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Session Length (hours)
                                        </label>
                                        <input
                                            type="number"
                                            value={sessionLength}
                                            onChange={(e) => setSessionLength(Number(e.target.value))}
                                            className="input-field"
                                            min="0.5"
                                            step="0.5"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Available Seats
                                        </label>
                                        <input
                                            type="number"
                                            value={sessionSeats}
                                            onChange={(e) => setSessionSeats(Number(e.target.value))}
                                            className="input-field"
                                            min="1"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Token Cost
                                        </label>
                                        <input
                                            type="number"
                                            value={sessionTokenCost}
                                            onChange={(e) => setSessionTokenCost(Number(e.target.value))}
                                            className="input-field"
                                            min="1"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={handleAddSession}
                                className="btn-primary flex items-center"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Plan Sessions
                            </button>
                            <button
                                onClick={handleSubmitSessions}
                                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                                Submit All Sessions ({sessionsToAdd.length})
                            </button>
                            <button
                                onClick={handleClearAddForm}
                                className="btn-outline"
                            >
                                Clear All
                            </button>
                        </div>

                        {/* Sessions Preview */}
                        {sessionsToAdd.length > 0 && (
                            <div className="card p-6">
                                <h3 className="text-lg font-semibold text-white mb-4">
                                    Planned Sessions ({sessionsToAdd.length})
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-700">
                                        <thead className="table-header">
                                            <tr>
                                                <th className="px-6 py-3 text-left">Day</th>
                                                <th className="px-6 py-3 text-left">Date</th>
                                                <th className="px-6 py-3 text-left">Time</th>
                                                <th className="px-6 py-3 text-left">Length</th>
                                                <th className="px-6 py-3 text-left">Seats</th>
                                                <th className="px-6 py-3 text-left">Tokens</th>
                                                <th className="px-6 py-3 text-left">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-gray-900 divide-y divide-gray-700">
                                            {sessionsToAdd.map((session, index) => (
                                                <tr key={index} className="hover:bg-gray-800 transition-colors">
                                                    <td className="table-cell">{session.day}</td>
                                                    <td className="table-cell">{session.date.format('YYYY-MM-DD')}</td>
                                                    <td className="table-cell">{session.time.format('HH:mm')}</td>
                                                    <td className="table-cell">{session.length}h</td>
                                                    <td className="table-cell">{session.seats}</td>
                                                    <td className="table-cell">{session.tokenCost}</td>
                                                    <td className="table-cell">
                                                        <button
                                                            onClick={() => handleRemoveSessionToAdd(index)}
                                                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                                        >
                                                            <TrashIcon className="h-5 w-5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Manage Sessions Tab */}
                {activeTab === 'manage' && (
                    <div className="space-y-6">
                        {/* Date Range Filter */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Date Range</h3>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            From Date
                                        </label>
                                        <DatePicker
                                            value={fromDate}
                                            format="DD/MM/YYYY"
                                            onChange={(newValue) => setFromDate(newValue)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: "outlined"
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            To Date
                                        </label>
                                        <DatePicker
                                            value={toDate}
                                            format="DD/MM/YYYY"
                                            onChange={(newValue) => setToDate(newValue)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: "outlined"
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <button
                                        onClick={fetchExistingSessions}
                                        className="btn-primary"
                                    >
                                        <CalendarIcon className="h-4 w-4 mr-2 inline-block" />
                                        Refresh Sessions
                                    </button>
                                </div>
                            </LocalizationProvider>
                        </div>

                        {/* Smart Bulk Delete Filter */}
                        <div className="card p-6 bg-gradient-to-r from-red-900/20 to-gray-900">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                <FunnelIcon className="h-5 w-5 mr-2" />
                                Smart Bulk Delete - Remove Same Time Slot Across Weeks
                            </h3>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Day of Week
                                        </label>
                                        <select
                                            value={deleteFilterDay}
                                            onChange={(e) => setDeleteFilterDay(e.target.value)}
                                            className="input-field"
                                        >
                                            <option value="">Select day...</option>
                                            {daysOfWeek.map(day => (
                                                <option key={day} value={day}>{day}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Time Slot (HH:MM)
                                        </label>
                                        <input
                                            type="time"
                                            value={deleteFilterTime}
                                            onChange={(e) => setDeleteFilterTime(e.target.value)}
                                            className="input-field"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Start From Date
                                        </label>
                                        <DatePicker
                                            value={deleteFilterStartDate}
                                            format="DD/MM/YYYY"
                                            onChange={(newValue) => setDeleteFilterStartDate(newValue)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: "outlined"
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Number of Weeks
                                        </label>
                                        <input
                                            type="number"
                                            value={deleteFilterWeeks}
                                            onChange={(e) => setDeleteFilterWeeks(Number(e.target.value))}
                                            className="input-field"
                                            min="1"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4 flex gap-3">
                                    <button
                                        onClick={handleSmartBulkDelete}
                                        className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                                    >
                                        Select Matching Sessions
                                    </button>
                                    <button
                                        onClick={handleFilterByDayAndTime}
                                        className="btn-outline"
                                    >
                                        Filter View
                                    </button>
                                    <button
                                        onClick={() => {
                                            setDeleteFilterDay('');
                                            setDeleteFilterTime('');
                                            setFilteredSessions(existingSessions);
                                        }}
                                        className="btn-outline"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </LocalizationProvider>
                        </div>

                        {/* Bulk Seat Update */}
                        <div className="card p-6 bg-gradient-to-r from-blue-900/20 to-gray-900">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                <CalendarIcon className="h-5 w-5 mr-2" />
                                Bulk Update Seats - Update Selected Sessions
                            </h3>
                            <p className="text-sm text-gray-400 mb-4">
                                Update the available seat count for all selected sessions. The system will prevent reducing seats below already booked counts.
                            </p>
                            <div className="flex items-end gap-4">
                                <div className="flex-1 max-w-xs">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        New Available Seats
                                    </label>
                                    <input
                                        type="number"
                                        value={bulkSeatCount}
                                        onChange={(e) => setBulkSeatCount(Number(e.target.value))}
                                        className="input-field"
                                        min="1"
                                        placeholder="Enter seat count"
                                    />
                                </div>
                                <button
                                    onClick={handleBulkUpdateSeats}
                                    disabled={selectedSessions.size === 0}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Update Seats for Selected ({selectedSessions.size})
                                </button>
                            </div>
                        </div>

                        {/* Bulk Actions */}
                        <div className="card p-6">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="text-white">
                                    <span className="text-2xl font-bold">{selectedSessions.size}</span>
                                    <span className="text-gray-400 ml-2">of {filteredSessions.length} sessions selected</span>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleSelectAll}
                                        className="btn-outline"
                                    >
                                        {selectedSessions.size === filteredSessions.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={selectedSessions.size === 0}
                                        className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <TrashIcon className="h-4 w-4 mr-2 inline-block" />
                                        Delete Selected ({selectedSessions.size})
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Sessions Table */}
                        {loading ? (
                            <div className="card p-8 text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                                <p className="text-gray-400 mt-4">Loading sessions...</p>
                            </div>
                        ) : filteredSessions.length === 0 ? (
                            <div className="card p-8 text-center">
                                <CalendarIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-gray-300 mb-2">No Sessions Found</h3>
                                <p className="text-gray-500">Try adjusting your date range or filters.</p>
                            </div>
                        ) : (
                            <div className="card overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-700">
                                        <thead className="table-header">
                                            <tr>
                                                <th className="px-6 py-3 text-left">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSessions.size === filteredSessions.length && filteredSessions.length > 0}
                                                        onChange={handleSelectAll}
                                                        className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                                    />
                                                </th>
                                                <th className="px-6 py-3 text-left">Name</th>
                                                <th className="px-6 py-3 text-left">Date & Day</th>
                                                <th className="px-6 py-3 text-left">Time</th>
                                                <th className="px-6 py-3 text-left">Duration</th>
                                                <th className="px-6 py-3 text-left">Seats</th>
                                                <th className="px-6 py-3 text-left">Tokens</th>
                                                <th className="px-6 py-3 text-left">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-gray-900 divide-y divide-gray-700">
                                            {filteredSessions.map((session) => {
                                                const startDate = new Date(session.startDate);
                                                const endDate = new Date(startDate.getTime() + session.numberOfMinuets * 60000);
                                                const dayName = daysOfWeek[startDate.getDay() === 0 ? 6 : startDate.getDay() - 1];

                                                return (
                                                    <tr
                                                        key={session.id}
                                                        className={`hover:bg-gray-800 transition-colors ${
                                                            selectedSessions.has(session.id) ? 'bg-blue-900/20' : ''
                                                        }`}
                                                    >
                                                        <td className="table-cell">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedSessions.has(session.id)}
                                                                onChange={() => handleSelectSession(session.id)}
                                                                className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td className="table-cell">
                                                            <div className="font-medium text-white">{session.name}</div>
                                                        </td>
                                                        <td className="table-cell">
                                                            <div className="text-white">{format(startDate, 'yyyy-MM-dd')}</div>
                                                            <div className="text-sm text-gray-400">{dayName}</div>
                                                        </td>
                                                        <td className="table-cell">
                                                            <div className="text-white">{format(startDate, 'HH:mm')}</div>
                                                            <div className="text-sm text-gray-400">to {format(endDate, 'HH:mm')}</div>
                                                        </td>
                                                        <td className="table-cell">
                                                            <span className="text-white">{session.numberOfMinuets} min</span>
                                                        </td>
                                                        <td className="table-cell">
                                                            <span className="text-white">{session.seatsTaken || 0} / {session.availableSeats}</span>
                                                        </td>
                                                        <td className="table-cell">
                                                            <span className="text-white">{session.tokenCost}</span>
                                                        </td>
                                                        <td className="table-cell">
                                                            <button
                                                                onClick={async () => {
                                                                    const confirmed = window.confirm(`Delete session "${session.name}" on ${format(startDate, 'yyyy-MM-dd')}?`);
                                                                    if (!confirmed) return;

                                                                    const token = await Helper.getCookie('wh-jwt');
                                                                    try {
                                                                        const response = await fetch(`${baseUrl}/api/sessions/admin/delete/${session.id}`, {
                                                                            method: 'POST',
                                                                            headers: {
                                                                                'Authorization': `Bearer ${token}`,
                                                                                'Content-Type': 'application/json'
                                                                            },
                                                                            credentials: 'include'
                                                                        });

                                                                        if (response.ok) {
                                                                            alert('Session deleted successfully');
                                                                            await fetchExistingSessions();
                                                                        } else {
                                                                            alert('Failed to delete session');
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('Error deleting session', error);
                                                                        alert('Failed to delete session');
                                                                    }
                                                                }}
                                                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                                            >
                                                                <TrashIcon className="h-5 w-5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </LayoutWrapper>
    );
};

export default BulkSessionManagement;

